import { ConnectionTypeName } from '../connections/connection.models';
import { ConditionOperator } from '../constants/conditions_operator_picker';
import { DesignerSchemaFieldI } from './models/designer-schema-field.model';

export type XYCoords = [number, number];

export enum COMPONENT_PREVIEWER_STATUSES {
  creating = 'creating',
  failed = 'failed',
  stopped = 'stopped',
  pending = 'pending',
  running = 'running',
  provisioning = 'provisioning',
  completed = 'completed',
  validating = 'validating',
}

interface ComponentPreviewLogError {
  message: string;
  field?: string;
  component_id?: string;
}

export interface ComponentPreviewLogMessage {
  id: number;
  status: COMPONENT_PREVIEWER_STATUSES;
  status_message: string;
  account_id: number;
  owner_id: number;
  runtime?: string;
  errors: Array<ComponentPreviewLogError | string>;
  created_at: string;
  updated_at: string;
  progress: number;
  package_id: number;
  owner: any;
  type: string;
}

export interface ComponentPreviewResultData {
  data?: any;
  fields?: any;
  raw?: any;
  error?: string;
  info?: string;
}

export interface PackagePreviewPayload {
  data_flow_json: {
    components: Component[];
    edges: Edge[];
  };
  destination_schema: Schema;
  variables: Variables;
  source_schema_importer_parameters: any;
  package_id: number;
}

export interface Variables {
  [key: string]: string;
}

export interface Field {
  name: string;
  projected_name?: string;
  alias?: string;
  data_type?: string;
  order_type?: string;
  column_name?: string;
}

export interface ExpressionField {
  left: string;
  right: string;
  operator: ConditionOperator;
}

export interface WindowedField {
  alias: string;
  default_value: string;
  function_name: string;
  field_name: string;
  ntiles: string;
  offset: string;
  range_after: string;
  range_before: string;
}

export interface GroupedField {
  field_name: string;
}

export interface AggregatedField {
  field_name: string;
  alias: string;
  function_name: string;
  projected_field_name: string;
}

export interface Relation {
  fields: string[];
}

export interface PredicateExpression {
  left: string;
  operator: ConditionOperator;
  right: string;
}

export interface Predicate {
  match_type: string;
  expressions: PredicateExpression[];
}

export interface ColumnMap {
  column_name: string;
  field_name: string;
  is_merge_key: boolean;
  is_bson?: boolean;
  field_type?: string;
  is_reference_key?: boolean;
  relationship_field_name?: string;
  relationship_field_type?: string;
  relationship_name?: string;
  objectField?: DesignerSchemaFieldI;
}

export interface Connection {
  id: number;
  name: string;
  type: ConnectionTypeName;
  unique_id?: string;
}

export interface Schema {
  valid: boolean;
  fields: Array<Field & ConnectionSchemaField>;
  name?: string;
  id?: string;
}

export interface NoteComponentData extends CommonComponentData {
  description: string;
  noteHeight?: number;
  noteWidth?: number;
}
export interface NoteComponent {
  sticky_note_component: DatabaseDestinationComponentData;
}

export interface DatabaseDestinationComponentData extends CommonComponentData {
  add_columns: boolean;
  batch_size: number;
  column_mappings: ColumnMap[];
  connection: Connection;
  create_table: boolean;
  database_connection_id: string;
  operation_type: string;
  parallel: number;
  schema: Schema;
  schema_name: string;
  single_transaction_per_batch: false;
  split_by_field: string;
  table_name: string;
  warehouse?: string;
  database?: string;
  pre_action_sql?: string;
  post_action_sql?: string;
  identity_insert_sql_server?: boolean;
  comp_update?: string;
  explicit_ids?: boolean;
  round_decimals?: boolean;
  replacement_char?: string;
  intermediate_compression_type?: string;
  max_errors?: number;
  max_bad_records?: number;
  truncate_columns?: boolean;
  trim_blanks?: boolean;
  empty_as_null?: boolean;
  blank_as_null?: boolean;
  null_string?: string;
  comp_rows?: number;
}
export interface DatabaseDestinationComponent {
  database_destination_component: DatabaseDestinationComponentData;
}

export interface DatabaseSourceComponentData extends CommonComponentData {
  access_mode: 'query' | 'table';
  before_action: string;
  connection: Connection;
  database_connection_id: string;
  parallel: number;
  query: string;
  schema: Schema;
  schema_name: string;
  split_by_column_name: string;
  table_name: string;
  where_clause: string;
}

export interface CSVDestinationType {
  escape_character: string;
  field_delimiter: string;
  line_ending?: string;
  text_qualifier: string;
  write_header?: boolean;
  skip_header?: boolean;
}

export interface CloudStorageSourceComponentData extends CommonComponentData {
  bucket: string;
  char_encoding: string;
  record_settings: CSVDestinationType;
  json_path: string;
  path: string;
  record_delimiter: string;
  record_type: string;
  source_path_field_alias: string;
  schema: Schema;
  before_action: string;
  manifest_path?: string;
  manifest_connection?: Connection;
  destination_type: {
    csv_destination_type?: CSVDestinationType;
    json_destination_type?: {
      json_destination_type: boolean;
    };
    parquet_destination_type?: {
      parquet_destination_type: boolean;
    };
  };
}
export interface CloudStorageSourceComponent {
  cloud_storage_source_component: CloudStorageSourceComponentData;
}

export interface CloudStorageDestinationComponentData extends CommonComponentData {
  destination_type: {
    csv_destination_type?: CSVDestinationType;
    json_destination_type?: {
      json_destination_type: boolean;
    };
    parquet_destination_type?: {
      parquet_destination_type: boolean;
    };
  };
  file_names: 'default' | 'custom';
  prefix: string;
  suffix: string;
  delete_destination_path?: boolean;
  destination_exists_action?: string;
  compression_type: string;
  enforce_single_file: boolean;
}
export interface CloudStorageDestinationComponent {
  cloud_storage_source_component: CloudStorageDestinationComponentData;
}

export interface FacebookAdsInsightsSourceComponentData extends CommonComponentData {
  api_version: string;
  level: string;
  action_report_time: string;
  time_increment: string;
  report_date_range_type: string;
  filtering: string;
  report_on: string;
  ad_accounts: string[];
  report_date_range_min?: string;
  report_date_range_max?: string;
  schema: Schema;
  manual_breakdown?: 'DayByDay' | 'Default';
  with_custom_fields?: boolean;
}
export interface FacebookAdsInsightsSourceComponent {
  facebook_ads_insights_source_component: FacebookAdsInsightsSourceComponentData;
}

export interface AnalyticsSourceComponentData extends CommonComponentData {
  report_on: string;
  profile_ids: string;
  report_date_range_min: string;
  report_date_range_max: string;
  metrics: string[];
  dimensions: string[];
  segment: string;
  sort: string;
  filters: string;
  sampling_level: string;
  include_empty?: boolean;
  schema: Schema;
  profile_id_field_alias: string;
  account_name_field_alias: string;
  property_name_field_alias: string;
  profile_name_field_alias: string;
  report_sampling_level: string;
  include_empty_rows: boolean;
}
export interface AnalyticsSourceComponent {
  analytics_source_component: AnalyticsSourceComponentData;
}

export interface Analytics4SourceComponentData extends CommonComponentData {
  api_version: string;
  report_on: string;
  property_ids: string;
  report_date_range_min: string;
  report_date_range_max: string;
  metrics: string[];
  dimensions: string[];
  include_empty?: boolean;
  schema: Schema;
  property_id_field_alias: string;
  account_name_field_alias: string;
  property_name_field_alias: string;
  account_id_field_alias: string;
  report_sampling_level: string;
  include_empty_rows: boolean;
}
export interface Analytics4SourceComponent {
  analytics_ga4_source_component: Analytics4SourceComponentData;
}

export interface BingAdsSourceComponentData extends CommonComponentData {
  time_period_hour_field_alias: string;
  schema: Schema;
  time_period_date_field_alias: string;
  report_type: string;
  accounts_campaigns?: string;
  report_aggregation_type: string;
  report_date_range_type: string;
  hasTimePeriodField?: boolean;
}
export interface BingAdsSourceComponent {
  analytics_source_component: BingAdsSourceComponentData;
}

export interface BigQuerySourceComponentData extends CommonComponentData {
  use_legacy_sql: boolean;
  stored_procedure: boolean;
}
export interface BigQuerySourceComponent {
  big_query_source_component: BigQuerySourceComponentData;
}

export interface AdwordsSourceComponentData extends CommonComponentData {
  api_version: string;
  report_type: string;
  report_date_range_field?: string;
  report_date_range_type: string;
  report_date_range_min: string;
  report_date_range_max: string;
  customer_id_field_alias: string;
  customer_ids: string;
  report_on: string;
}
export interface AdwordsSourceComponent {
  adwords_source_component: AdwordsSourceComponentData;
}

export interface SelectComponentData extends CommonComponentData {
  fields: Field[];
}
export interface SelectComponent {
  select_component: SelectComponentData;
}

export interface SortComponentData extends CommonComponentData {
  fields: Field[];
}
export interface SortComponent {
  sort_component: SortComponentData;
}

export interface RankComponentData extends CommonComponentData {
  dense: boolean;
  field_alias: string;
  fields: Field[];
  data_order: 'no_sort' | 'sort';
}
export interface RankComponent {
  rank_component: RankComponentData;
}

export interface LimitComponentData extends CommonComponentData {
  n: number;
  partitioned_fields: Field[];
  ordered_fields: Field[];
  partitioning: 'partition_by' | 'all';
  order: 'no_sort' | 'sort';
}
export interface LimitComponent {
  limit_component: LimitComponentData;
}

export interface WindowComponentData extends CommonComponentData {
  partitioned_fields: Field[];
  ordered_fields: Field[];
  windowed_fields: WindowedField[];
  grouping_type: string;
  data_order: string;
}
export interface WindowComponent {
  window_component: WindowComponentData;
}

export interface SampleComponentData extends CommonComponentData {
  percentage: number;
}
export interface SampleComponent {
  sample_component: SampleComponentData;
}

export interface JoinComponentData extends CommonComponentData {
  join_type: string;
  optimization_type: string;
  relations: Relation[];
}
export interface JoinComponent {
  join_component: JoinComponentData;
}

export interface CrossJoinComponentData extends CommonComponentData {}
export interface CrossJoinComponent {
  cross_join_component: CrossJoinComponentData;
}

export interface CloneComponentData extends CommonComponentData {}
export interface CloneComponent {
  clone_component: CloneComponentData;
}

export interface UnionComponentData extends CommonComponentData {
  fields: Field[];
}
export interface UnionComponent {
  union_component: UnionComponentData;
}

export interface FilterComponentData extends CommonComponentData {
  predicates: Predicate[] | Predicate;
}
export interface FilterComponent {
  filter_component: FilterComponentData;
}

export interface AssertComponentData extends CommonComponentData {
  predicates: Predicate[] | Predicate;
  message: string;
}
export interface AssertComponent {
  assert_component: AssertComponentData;
}

export interface AggregateComponentData extends CommonComponentData {
  grouped_fields: GroupedField[];
  aggregated_fields: AggregatedField[];
  grouping_type: string;
}
export interface AggregateComponent {
  aggregate_component: AggregateComponentData;
}

export interface CubeComponentData extends CommonComponentData {
  grouped_fields: GroupedField[];
  aggregated_fields: AggregatedField[];
}
export interface CubeComponent {
  cube_component: CubeComponentData;
}

export interface DistinctComponentData extends CommonComponentData {}
export interface DistinctComponent {
  distinct_component: DistinctComponentData;
}

export interface RestAPIHeader {
  name: string;
  value: string;
}

export interface RestApiSourceComponentData extends CommonComponentData {
  authentication?: string;
  body: string;
  headers: RestAPIHeader[];
  url: string;
  json_path: string;
  connection: Connection | {};
  schema: Schema;
  rest_api_connection_id: string;
  method: string;
  username: string;
  password: string;
  response_type: string;
  use_pagination: boolean;
  pagination_scheme: string;
  pagination_sleep_interval: number;
  max_requests: string;
}
export interface RestApiSourceComponent {
  rest_api_source_component: RestApiSourceComponentData;
}

export interface SalesforceSourceComponentData extends CommonComponentData {
  object_name: string;
  where_clause: string;
  schema: Schema;
  query: string;
  access_mode: 'query' | 'object';
  is_chunk_size_enabled: boolean;
  chunk_size: number;
  source_action: string;
}
export interface SalesforceSourceComponent {
  salesforce_source_component: SalesforceSourceComponentData;
}

export interface SalesforceDestinationComponentData extends CommonComponentData {
  operation_type: string;
  id_field: string;
  batch_size: number;
  max_errors: number;
  errors_output_connection?: Connection;
  track_errors?: boolean;
  enable_null_value?: boolean;
  errors_output_path?: string;
  column_mappings: ColumnMap[];
  object_name: string;
}
export interface SalesforceDestinationComponent {
  salesforce_destination_component: SalesforceDestinationComponentData;
}

export interface ExecuteSQLComponentData extends CommonComponentData {
  operator: 'and' | 'or';
  result_variable: string;
  result_type: string;
  query: string;
}
export interface ExecuteSQLComponent {
  execute_sql_component: ExecuteSQLComponentData;
}

export interface RunPackageComponentData extends CommonComponentData {
  package_id: number;
  package_name: string;
  variables: Variables;
  operator: 'or' | 'and';
}
export interface RunPackageComponent {
  run_package_component: RunPackageComponentData;
}

export interface DatabaseSourceComponent {
  database_source_component: DatabaseSourceComponentData;
}

export interface CommonComponentData {
  id: string;
  alias: string;
  name: string;
  xy: XYCoords;
  dynamic_connection?: string;
}

export type Component =
  | SelectComponent
  | DatabaseSourceComponent
  | DatabaseDestinationComponent
  | SortComponent
  | RankComponent
  | LimitComponent
  | WindowComponent
  | SampleComponent
  | JoinComponent
  | CrossJoinComponent
  | CloneComponent
  | UnionComponent
  | FilterComponent
  | AssertComponent
  | AggregateComponent
  | CubeComponent
  | DistinctComponent
  | DatabaseSourceComponent
  | DatabaseDestinationComponent
  | CloudStorageSourceComponent
  | FacebookAdsInsightsSourceComponent
  | AnalyticsSourceComponent
  | Analytics4SourceComponent
  | RestApiSourceComponent
  | SalesforceSourceComponent
  | AdwordsSourceComponent
  | BingAdsSourceComponent
  | BigQuerySourceComponent
  | CloudStorageDestinationComponent
  | SalesforceDestinationComponent
  | ExecuteSQLComponent
  | RunPackageComponent
  | NoteComponent;
export type AnyComponentData = SelectComponentData &
  SortComponentData &
  RankComponentData &
  LimitComponentData &
  WindowComponentData &
  SampleComponentData &
  JoinComponentData &
  CrossJoinComponentData &
  CloneComponentData &
  UnionComponentData &
  FilterComponentData &
  AssertComponentData &
  AggregateComponentData &
  CubeComponentData &
  DistinctComponentData &
  DatabaseSourceComponentData &
  DatabaseDestinationComponentData &
  CloudStorageSourceComponentData &
  FacebookAdsInsightsSourceComponentData &
  AnalyticsSourceComponentData &
  Analytics4SourceComponentData &
  RestApiSourceComponentData &
  SalesforceSourceComponentData &
  AdwordsSourceComponentData &
  BingAdsSourceComponentData &
  BigQuerySourceComponentData &
  CloudStorageDestinationComponentData &
  SalesforceDestinationComponentData &
  ExecuteSQLComponentData &
  RunPackageComponentData &
  NoteComponentData;

export interface Edge {
  id: string;
  order: number;
  source: string;
  source_index: number;
  target: string;
  label?: string;
  event?: EdgeEvent;
}

export interface Position {
  x: number;
  y: number;
}

export enum EdgeEvent {
  success = 'success',
  failure = 'failure',
  completion = 'completion',
}

export interface ExtendedEdge extends Edge {
  sourcePosition: Position;
  sourceCurvePosition: Position;
  targetPosition: Position;
  targetCurvePosition: Position;
  togglePosition: Position;
  event?: EdgeEvent;
}

export type SchemaData = Array<boolean | string | number | string[]>;

export interface FieldDefinition {
  [key: string]: string;
}

export interface SchemaResponse {
  data?: SchemaData[];
  fields: FieldDefinition[];
  raw?: string;
}

export interface ConnectionSchemaField {
  name: string;
  data_type?: string;
  alias?: string;
  isDuplicateError?: boolean;
  focusedProp?: string;
  disableSortable?: boolean;
  disableTypeEdit?: boolean;
  required?: boolean;
  predefined?: boolean;
  _category?: string;
  category?: string;
  editable?: boolean;
  markedToBeRemoved?: boolean;
  id?: string;
}
